'use client';

import { useState, useEffect, useCallback } from 'react';
import { useReveal } from '@/hooks/use-reveal';
import { Star, Quote, ChevronLeft, ChevronRight, Award, Sparkles } from 'lucide-react';

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  city: string;
  rating: number;
  collection?: string;
  year?: number;
  featured?: boolean;
  category: 'salon' | 'atelier' | 'collection' | 'restauration';
}

const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "Mon sac ASMAO m'a été offert il y a huit ans. Huit ans plus tard, il est plus beau qu'au premier jour. La patine a transformé le cuir en une œuvre d'art vivante. Je ne pourrais plus m'en séparer.",
    name: 'Camille Vasseur',
    role: 'Collectionneuse',
    city: 'Paris 7e',
    rating: 5,
    collection: 'Sac de Ville',
    year: 2018,
    featured: true,
    category: 'collection',
  },
  {
    quote:
      "J'ai commandé une ceinture sur-mesure pour l'anniversaire de mon père. L'équipe a pris le temps de comprendre ce que je cherchais, m'a proposé trois cuirs différents. Le résultat a ému mon père aux larmes. Une maison qui respecte ses clients.",
    name: 'Omar Benkacem',
    role: 'Architecte DPLG',
    city: 'Lyon',
    rating: 5,
    collection: 'Ceinture Couture',
    year: 2022,
    category: 'atelier',
  },
  {
    quote:
      "En tant qu'homme d'affaires, j'apprécie l'excellence discrète. Mes portefeuilles ASMAO sont devenus mes compagnons de voyage. Le cuir est noble, la finition irréprochable, et le service d'une courtoisie rare. Une vraie maison parisienne.",
    name: 'Thierry Lemoine',
    role: "Directeur d'investissements",
    city: 'Paris 8e',
    rating: 5,
    collection: 'Maroquinerie',
    year: 2020,
    category: 'collection',
  },
  {
    quote:
      "Mon mari m'a offert un sac commandé au Salon Privé. Chaque détail — le choix du cuir, la patine cognac, la doublure en soie — porte la marque d'une écoute rare. Ce sac raconte notre histoire. C'est cela, le vrai luxe.",
    name: 'Isabelle de Rochefort',
    role: 'Collectionneuse privée',
    city: 'Genève',
    rating: 5,
    collection: 'Sac Salon Privé',
    year: 2024,
    featured: true,
    category: 'salon',
  },
  {
    quote:
      "Vingt-cinq ans de métier se ressentent dans chaque couture. J'ai vu des centaines de prétendues maisons de luxe. Celle-ci est différente : ici, l'artisan parle au client, écoute, propose. Le reste n'est que production.",
    name: 'Frédéric Marchand',
    role: 'Expert en art décoratif',
    city: 'Paris 6e',
    rating: 5,
    collection: "Manteau d'Atelier",
    year: 2023,
    category: 'atelier',
  },
  {
    quote:
      "J'ai fait restaurer une valise de mon grand-père. Elle était dans un état lamentable. Ce qu'ils ont rendu dépasse l'état neuf : ils ont préservé la mémoire, effacé les blessures, gardé l'âme. Je n'ai pas de mots.",
    name: 'Hélène Brémont',
    role: 'Héritière, famille industrielle',
    city: 'Bordeaux',
    rating: 5,
    collection: 'Restauration',
    year: 2024,
    category: 'restauration',
  },
  {
    quote:
      "J'ai rencontré Asmao par hasard, en cherchant un ceinturon pour une cavalière. Ce que j'ai trouvé, c'est une artisan qui écoute la voix du cuir. Elle m'a expliqué pourquoi ce cuir-là, pourquoi cette couture, pourquoi ce délai. Jamais je n'avais senti autant de respect pour le client.",
    name: 'Margaux de Vignolles',
    role: 'Éleveuse de chevaux',
    city: 'Saumur',
    rating: 5,
    collection: 'Ceinture Équestre',
    year: 2023,
    featured: true,
    category: 'atelier',
  },
  {
    quote:
      "Commander un manteau Salon Privé a transformé ma vision du luxe. Trois rendez-vous, quatre mois d'attente, un objet qui me suit partout. Quand on me demande où je l'ai trouvé, je souris. Certaines portes ne s'ouvrent qu'à ceux qui savent attendre.",
    name: 'Alexandre Kowalski',
    role: "Chef d'orchestre",
    city: 'Vienne, Autriche',
    rating: 5,
    collection: 'Manteau Salon Privé',
    year: 2024,
    category: 'salon',
  },
  {
    quote:
      "Ils ont restauré mon cartable d'écolier des années 70. Cuir fendu, coutures parties, doublure en lambeaux. Aujourd'hui, il est plus solide qu'au premier jour, et il a conservé chaque marque de mon enfance. C'est de la magie, pas de la restauration.",
    name: 'Pierre-Étienne Lacombe',
    role: 'Notaire honoraire',
    city: 'Clermont-Ferrand',
    rating: 5,
    collection: 'Restauration Cartable',
    year: 2023,
    category: 'restauration',
  },
  {
    quote:
      "Mes deux filles ont hérité chacune d'un sac ASMAO que j'avais fait faire pour elles à leur majorité. Dix ans plus tard, les sacs vieillissent avec elles. C'est cela, transmettre : non pas un objet, mais un compagnon de vie.",
    name: 'Béatrice Fontana',
    role: 'Mère & Mécène',
    city: 'Bruxelles',
    rating: 5,
    collection: 'Sac Héritage',
    year: 2015,
    category: 'collection',
  },
];

const CATEGORIES = [
  { id: 'all', label: 'Toutes les Voix' },
  { id: 'salon', label: 'Salon Privé' },
  { id: 'atelier', label: "L'Atelier" },
  { id: 'collection', label: 'Collections' },
  { id: 'restauration', label: 'Restauration' },
] as const;

export default function Testimonials() {
  const { ref, visible } = useReveal();
  const [activeFilter, setActiveFilter] = useState<'all' | Testimonial['category']>('all');
  const [activeIdx, setActiveIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [previousFilter, setPreviousFilter] = useState<typeof activeFilter>('all');

  const filtered = activeFilter === 'all'
    ? TESTIMONIALS
    : TESTIMONIALS.filter(t => t.category === activeFilter);

  // Reset activeIdx when filter changes — render-phase check (not effect)
  if (previousFilter !== activeFilter) {
    setPreviousFilter(activeFilter);
    if (activeIdx !== 0) setActiveIdx(0);
  }

  const next = useCallback(() => {
    setActiveIdx((i) => (i + 1) % filtered.length);
  }, [filtered.length]);

  const prev = useCallback(() => {
    setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length);
  }, [filtered.length]);

  useEffect(() => {
    if (isPaused || filtered.length <= 1) return;
    const timer = setInterval(next, 7000);
    return () => clearInterval(timer);
  }, [next, isPaused, filtered.length]);

  const active = filtered[activeIdx] || filtered[0];

  return (
    <section
      id="temoignages"
      ref={ref}
      className="relative py-28 md:py-36 bg-[#F8F1E4] overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(43,24,16,0.6) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(ellipse at 50% 50%, rgba(201, 169, 97, 0.08) 0%, transparent 60%)',
        }}
      />

      <div className="container mx-auto px-6 relative">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className={`reveal ${visible ? 'visible' : ''} mb-6`}>
            <div className="flex items-center justify-center gap-4">
              <div className="w-10 h-px bg-[#8B4513]" />
              <span className="text-[#8B4513] font-serif-display text-sm tracking-[0.4em] uppercase">
                Témoignages
              </span>
              <div className="w-10 h-px bg-[#8B4513]" />
            </div>
          </div>

          <h2
            className={`reveal ${visible ? 'visible' : ''} font-display text-[#2B1810] text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.05] mb-8`}
          >
            La Voix de
            <br />
            <span className="italic text-luxe-gold">nos Clients</span>
          </h2>

          <p
            className={`reveal ${visible ? 'visible' : ''} text-[#5C2E03]/80 text-lg md:text-xl font-serif-display font-light leading-relaxed`}
          >
            Ce que disent celles et ceux qui ont choisi ASMAO STYLE CUIR. Des relations de
            confiance, parfois vieilles de dix ans, qui se tissent autour d'une passion commune :
            la belle matière.
          </p>
        </div>

        {/* Filter pills */}
        <div
          className={`reveal ${visible ? 'visible' : ''} flex flex-wrap items-center justify-center gap-2 mb-12 max-w-3xl mx-auto`}
        >
          {CATEGORIES.map((cat) => {
            const count = cat.id === 'all'
              ? TESTIMONIALS.length
              : TESTIMONIALS.filter(t => t.category === cat.id).length;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id as typeof activeFilter)}
                className={`relative px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-serif-display transition-all duration-400 border ${
                  activeFilter === cat.id
                    ? 'bg-[#2B1810] text-[#C9A961] border-[#2B1810]'
                    : 'bg-transparent text-[#5C2E03]/70 border-[#8B4513]/25 hover:border-[#C9A961] hover:text-[#2B1810]'
                }`}
              >
                {cat.label}
                <span className={`ml-2 text-[9px] ${
                  activeFilter === cat.id ? 'text-[#C9A961]/60' : 'text-[#5C2E03]/40'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Featured carousel */}
        <div
          className={`reveal ${visible ? 'visible' : ''} relative max-w-4xl mx-auto mb-16`}
        >
          <div className="flex items-center justify-center mb-8">
            <div className="w-12 h-px bg-[#C9A961]/40" />
            <div className="mx-3 w-2 h-2 border border-[#C9A961] rotate-45" />
            <div className="w-12 h-px bg-[#C9A961]/40" />
          </div>

          <div className="relative bg-white/70 backdrop-blur-sm p-8 md:p-14 border border-[#8B4513]/15 shadow-[0_10px_40px_-10px_rgba(43,24,16,0.15)]">
            <div className="absolute top-3 left-3 w-6 h-6 border-l border-t border-[#C9A961]/60" />
            <div className="absolute top-3 right-3 w-6 h-6 border-r border-t border-[#C9A961]/60" />
            <div className="absolute bottom-3 left-3 w-6 h-6 border-l border-b border-[#C9A961]/60" />
            <div className="absolute bottom-3 right-3 w-6 h-6 border-r border-b border-[#C9A961]/60" />

            {active.featured && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#C9A961] text-[#2B1810] text-[9px] tracking-[0.3em] uppercase font-bold flex items-center gap-1.5">
                <Award size={10} />
                Récit d'Exception
              </div>
            )}

            <Quote
              className="absolute -top-4 left-8 text-[#C9A961] bg-[#F8F1E4] p-1"
              size={42}
              strokeWidth={1}
            />

            <div className="flex justify-center gap-1 mb-6 mt-4">
              {Array.from({ length: active.rating }).map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  className="text-[#D4A017] fill-[#D4A017]"
                />
              ))}
            </div>

            <blockquote
              key={activeIdx}
              className="text-[#2B1810]/90 text-xl md:text-2xl lg:text-3xl font-serif-display font-light italic leading-relaxed text-center mb-8 animate-[cinematic-fade_0.7s_ease-out]"
            >
              « {active.quote} »
            </blockquote>

            <div className="text-center border-t border-[#8B4513]/15 pt-6">
              <div className="font-display text-xl text-[#2B1810] font-medium mb-1">
                {active.name}
              </div>
              <div className="text-[#8B4513] text-xs tracking-[0.25em] uppercase font-serif-display">
                {active.role} · {active.city}
              </div>
              {active.collection && (
                <div className="mt-3 inline-flex items-center gap-2 text-[#5C2E03]/60 text-[10px] tracking-[0.2em] uppercase font-serif-display">
                  <Sparkles size={10} className="text-[#C9A961]" />
                  {active.collection}
                  {active.year && ` · ${active.year}`}
                </div>
              )}
            </div>

            <button
              onClick={prev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 md:-translate-x-6 w-10 h-10 md:w-12 md:h-12 rounded-full border border-[#C9A961]/50 bg-[#F8F1E4] text-[#8B4513] hover:bg-[#C9A961] hover:text-[#2B1810] transition-all duration-300 flex items-center justify-center"
              aria-label="Témoignage précédent"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={next}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 md:translate-x-6 w-10 h-10 md:w-12 md:h-12 rounded-full border border-[#C9A961]/50 bg-[#F8F1E4] text-[#8B4513] hover:bg-[#C9A961] hover:text-[#2B1810] transition-all duration-300 flex items-center justify-center"
              aria-label="Témoignage suivant"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="flex items-center justify-center gap-2 mt-8 flex-wrap max-w-md mx-auto">
            {filtered.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIdx(idx)}
                className={`transition-all duration-500 ${
                  idx === activeIdx
                    ? 'w-8 h-1.5 bg-[#C9A961]'
                    : 'w-1.5 h-1.5 bg-[#8B4513]/25 hover:bg-[#8B4513]/50'
                }`}
                aria-label={`Témoignage ${idx + 1}`}
              />
            ))}
          </div>

          <div className="text-center mt-4 text-[#5C2E03]/60 text-xs tracking-[0.3em] uppercase font-serif-display">
            {String(activeIdx + 1).padStart(2, '0')} / {String(filtered.length).padStart(2, '0')}
          </div>
        </div>

        {/* Compact secondary testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {filtered.slice(0, 3).map((t, idx) => {
            const slideIdx = filtered.findIndex(x => x === t);
            return (
              <article
                key={t.name + idx}
                onClick={() => setActiveIdx(slideIdx)}
                className={`reveal ${visible ? 'visible' : ''} relative bg-white/50 backdrop-blur-sm p-6 border cursor-pointer transition-all duration-500 group ${
                  slideIdx === activeIdx
                    ? 'border-[#C9A961] shadow-[0_8px_30px_-10px_rgba(201,169,97,0.3)]'
                    : 'border-[#8B4513]/10 hover:border-[#C9A961]/60'
                }`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={11} className="text-[#D4A017] fill-[#D4A017]" />
                  ))}
                </div>
                <p className="text-[#2B1810]/80 text-sm font-serif-display font-light leading-relaxed italic mb-4 line-clamp-3">
                  « {t.quote} »
                </p>
                <div className="border-t border-[#8B4513]/10 pt-3">
                  <div className="font-display text-sm text-[#2B1810] font-medium">
                    {t.name}
                  </div>
                  <div className="text-[#8B4513] text-[10px] tracking-[0.2em] uppercase font-serif-display mt-1">
                    {t.role} · {t.city}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Trust badges */}
        <div
          className={`reveal ${visible ? 'visible' : ''} mt-20 flex flex-wrap items-center justify-center gap-8 md:gap-16 text-center`}
        >
          {[
            { num: '4 500+', label: 'Clients comblés' },
            { num: '25 ans', label: "D'expérience" },
            { num: '100%', label: 'Cuir véritable' },
            { num: '12 à 60h', label: 'Par pièce' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-display text-3xl md:text-4xl text-[#8B4513] font-medium">
                {stat.num}
              </div>
              <div className="text-[#5C2E03]/70 text-xs tracking-[0.25em] uppercase font-serif-display mt-2">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
